// NodosBoard.jsx

import React, {
    useMemo,
    useEffect,
    useRef,
    useState,
    useLayoutEffect,
    useCallback,
} from "react";
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    Handle,
    useNodesState,
    useEdgesState,
    MarkerType,
    useViewport,
} from "reactflow";
import "reactflow/dist/style.css";

import { FloatingEdge, FloatingConnectionLine } from "../Nodos/Floating";
import { Avatar, Stack } from "@mui/material";
import {
    AVATAR_SIZE,
    canvasCenter,
    canvasSize,
    layoutPersonaNodes,
    layoutPersonaNodesGrid,
    wrapTextByChars,
} from "../Nodos/helpers";

const ZOOM = 1.5;

function hashStringToInt(str) {
    let h = 2166136261 >>> 0; // FNV-like seed
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function mulberry32(seed) {
    let t = seed >>> 0;
    return function () {
        t += 0x6d2b79f5;
        let r = Math.imul(t ^ (t >>> 15), 1 | t);
        r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
}

const OrbitItem = ({
    seed,
    ringSize,
    baseRadius,
    label,
    bg,
    title,
    eccX,
    eccY,
    phaseDeg,
    radJitter,
    wobbleAmp,
    wobbleSpd,
    dirReverse,
}) => {
    // comment: element refs to update transforms without rerendering
    const wrapRef = React.useRef(null);
    const anchorRef = React.useRef(null);

    // comment: deterministic rng per item
    const rng = React.useMemo(() => mulberry32(seed), [seed]);

    // comment: angle and speed (degrees per second)
    const angleRef = React.useRef(phaseDeg); // start at given phase
    const speedRef = React.useRef((rng() * 90 + 30) * (dirReverse ? -1 : 1)); // initial speed 30..120 deg/s
    const targetRef = React.useRef(speedRef.current);

    // comment: smoothness / responsiveness (bigger = faster response to target)
    const accelPerSec = 120; // deg/s^2 equivalent for smoothing

    const invX = 1 / parseFloat(eccX || 1);
    const invY = 1 / parseFloat(eccY || 1);

    // comment: pick new random target speed at random intervals
    React.useEffect(() => {
        let live = true;

        const scheduleNext = () => {
            if (!live) return;
            const delay = 1200 + Math.floor(rng() * 2600); // 1.2s..3.8s
            const handle = setTimeout(() => {
                const newMag = 20 + rng() * 160; // 20..180 deg/s
                // comment: randomly keep or flip direction slightly biased to keep current
                const keepDir =
                    rng() < 0.7
                        ? Math.sign(speedRef.current || 1)
                        : rng() < 0.5
                        ? -1
                        : 1;
                targetRef.current = newMag * keepDir;
                scheduleNext();
            }, delay);
            timers.push(handle);
        };

        const timers = [];
        scheduleNext();
        return () => {
            live = false;
            timers.forEach(clearTimeout);
        };
    }, [rng]);

    // comment: main RAF loop to tween speed and rotate
    React.useEffect(() => {
        let rafId;
        let last = performance.now();

        const tick = (now) => {
            const dt = Math.max(0, Math.min(0.05, (now - last) / 1000)); // clamp dt 0..50ms
            last = now;

            // comment: ease current speed towards target
            const cur = speedRef.current;
            const tgt = targetRef.current;
            const diff = tgt - cur;
            const maxStep = accelPerSec * dt;
            const step =
                Math.abs(diff) < maxStep ? diff : Math.sign(diff) * maxStep;
            const nextSpeed = cur + step;
            speedRef.current = nextSpeed;

            // comment: integrate angle
            angleRef.current += nextSpeed * dt;

            // comment: apply transforms
            if (wrapRef.current) {
                // comment: rotation around center
                wrapRef.current.style.transform = `rotate(${angleRef.current}deg)`;
            }
            if (anchorRef.current) {
                // comment: keep the initial phase offset visually consistent (handled by starting angle)
                // nothing else needed here, anchor stays as structure hook
            }

            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, []);

    return (
        <div
            ref={wrapRef}
            className="orbit-item-wrap"
            style={{
                // comment: rotation now handled by JS; keep pointer-events none via parent
                ["--spd"]: "0s",
                ["--dir"]: "normal",
            }}
            title={title || ""}
        >
            <div
                className="orbit-path"
                style={{ ["--eccX"]: eccX, ["--eccY"]: eccY }}
            >
                <div
                    ref={anchorRef}
                    className="orbit-anchor"
                    style={{
                        ["--phase"]: `${0}deg` /* comment: phase baked in angleRef start */,
                    }}
                >
                    <div
                        className="orbit-body"
                        style={{
                            ["--radius"]: `${radJitter}px`,
                            ["--amp"]: wobbleAmp,
                            ["--wspd"]: wobbleSpd,
                        }}
                    >
                        {/* comment: this wrapper cancels the non-uniform scale applied by .orbit-path */}
                        <div
                            className="orbit-unscale"
                            style={{ transform: `scale(${invX}, ${invY})` }}
                        >
                            <div
                                className="orbit-item"
                                style={{ background: bg }}
                            >
                                {label}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* comment: CSS stays almost identical; remove spin animation dependency expectation */
const OrbitCSS = () => (
    <style>{`
    @keyframes radialWobble {
      0%   { transform: translateX(calc(var(--radius, 34px) - var(--amp, 4px))); }
      50%  { transform: translateX(calc(var(--radius, 34px) + var(--amp, 4px))); }
      100% { transform: translateX(calc(var(--radius, 34px) - var(--amp, 4px))); }
    }

    .avatar-wrap { position: relative; display: inline-block; }

    .orbit-wrap {
      position: absolute;
      top: 50%; left: 50%;
      width: calc(var(--ring, 40px) * 2);
      height: calc(var(--ring, 40px) * 2);
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .orbit-item-wrap {
      position: absolute; inset: 0;
      transform-origin: 50% 50%;
      /* comment: rotation is applied inline by JS with style.transform */
    }

    .orbit-path {
      position: absolute; inset: 0;
      transform: scale(var(--eccX, 1), var(--eccY, 1));
      transform-origin: 50% 50%;
    }

    .orbit-anchor {
      position: absolute; top: 50%; left: 50%;
      transform-origin: 50% 50%;
    }

    .orbit-body {
      position: absolute; top: -50%; left: 0;
      animation: radialWobble var(--wspd, 4s) ease-in-out infinite;
    }

    .orbit-item {
      position: absolute; top: 50%; left: 50%;
      width: 18px; height: 18px; border-radius: 50%;
      display:flex; align-items:center; justify-content:center;
      font-size:10px; color:#fff;
      box-shadow:0 0 0 1px rgba(0,0,0,0.08) inset;
      transform: translate(-50%, -50%);
    }

    .orbit-unscale {
        position: absolute;
        inset: 0;
        transform-origin: 50% 50%;
    }
  `}</style>
);

const NodoPersona = ({ data, selected }) => {
    const groups = data?.groups || [];
    const baseRadius = 34;
    const personaKey =
        String(data?.displayName || "") + "|" + String(groups.length || 0);
    const baseSeed = hashStringToInt(personaKey);
    const baseRng = mulberry32(baseSeed);
    const ringSize = baseRadius + 10 + Math.floor(baseRng() * 8);

    const avatarRef = useRef(null);
    const [avatarCenter, setAvatarCenter] = useState({ x: 0, y: 0 });
    const onAvatarCenterChange = data?.onAvatarCenterChange;
    const personaId = data?.personaId;
    useLayoutEffect(() => {
        if (!avatarRef.current) return;

        const rect = avatarRef.current.getBoundingClientRect();
        const parent = avatarRef.current.offsetParent?.getBoundingClientRect();
        if (!parent) return;

        const center = {
            x: rect.left - parent.left + rect.width / 2,
            y: rect.top - parent.top + rect.height / 2,
        };

        // local state (por si quieres debug en el nodo)
        setAvatarCenter(center);

        // NEW: notify parent so it can use avatar center for layouts
        if (onAvatarCenterChange && personaId != null) {
            onAvatarCenterChange(String(personaId), center);
        }
    }, [onAvatarCenterChange, personaId]);

    return (
        <div style={{ position: "relative" }}>
            <Stack direction="column" alignItems="center">
                <div className="avatar-wrap">
                    <Avatar
                        ref={avatarRef}
                        alt={data.displayName}
                        src={data.photoUrl || undefined}
                        sx={{
                            width: AVATAR_SIZE,
                            height: AVATAR_SIZE,
                            fontSize: 14,
                            border: selected
                                ? "2px solid #1976d2"
                                : "2px solid transparent",
                        }}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                    >
                        {data?.displayName
                            ? data.displayName.charAt(0).toUpperCase()
                            : "?"}
                    </Avatar>

                    {selected && groups.length > 0 && (
                        <>
                            <OrbitCSS />
                            <div
                                className="orbit-wrap"
                                style={{ ["--ring"]: `${ringSize}px` }}
                            >
                                {groups.slice(0, 8).map((g, i) => {
                                    const seed = hashStringToInt(
                                        String(g.grupo_id) +
                                            "|" +
                                            i +
                                            "|" +
                                            personaKey
                                    );
                                    const rng = mulberry32(seed);

                                    const eccX = (0.75 + rng() * 0.4).toFixed(
                                        2
                                    );
                                    const eccY = (0.75 + rng() * 0.4).toFixed(
                                        2
                                    );
                                    const phase = Math.floor(rng() * 360); // baked into initial angle
                                    const radJitter =
                                        baseRadius + Math.floor(rng() * 10 - 5);
                                    const wobbleAmp =
                                        (3 + rng() * 5).toFixed(1) + "px";
                                    const wobbleSpd =
                                        (2 + rng() * 4).toFixed(2) + "s";
                                    const bg = g.color || "#999";
                                    const label =
                                        (g.icon && String(g.icon).trim()) ||
                                        g.nombre?.[0]?.toUpperCase?.() ||
                                        "•";
                                    const dirReverse = rng() < 0.5;

                                    return (
                                        <OrbitItem
                                            key={g.grupo_id + "_" + i}
                                            seed={seed}
                                            ringSize={ringSize}
                                            baseRadius={baseRadius}
                                            label={
                                                label.length <= 2
                                                    ? label
                                                    : label.slice(0, 2)
                                            }
                                            bg={bg}
                                            title={g.nombre || ""}
                                            eccX={eccX}
                                            eccY={eccY}
                                            phaseDeg={phase}
                                            radJitter={radJitter}
                                            wobbleAmp={wobbleAmp}
                                            wobbleSpd={wobbleSpd}
                                            dirReverse={dirReverse}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                <strong style={{ textAlign: "center", lineHeight: 1.15 }}>
                    {wrapTextByChars(data?.displayName ?? "", 14).map(
                        (ln, i) => (
                            <span key={i} style={{ display: "block" }}>
                                {ln}
                            </span>
                        )
                    )}
                </strong>
            </Stack>

            <Handle
                type="target"
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#fff",
                    border: "1px solid #555",
                }}
            />
            <Handle
                type="source"
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#fff",
                    border: "1px solid #555",
                }}
            />
        </div>
    );
};

const nodeTypes = { nodoPersona: NodoPersona };
const edgeTypes = { floating: FloatingEdge };

const ViewportDebug = () => {
    const { x, y, zoom } = useViewport();

    return (
        <div
            style={{
                position: "absolute",
                bottom: 8,
                left: 45,
                padding: "4px 8px",
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                fontSize: 11,
                borderRadius: 4,
                pointerEvents: "none",
                zIndex: 9999,
            }}
        >
            x: {Math.round(x)} | y: {Math.round(y)} | zoom: {zoom.toFixed(2)}
        </div>
    );
};

const DEV = false;
// comment: SVG overlay that follows ReactFlow viewport transform
const ClusterRingsOverlay = ({ clusterMeta, clusterCenters, gruposById }) => {
    const { x, y, zoom } = useViewport();
    if (!DEV) return null;

    if (!clusterMeta?.length || !clusterCenters?.length) return null;

    return (
        <svg
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 2,
            }}
        >
            {/* comment: match ReactFlow's transform: translate(x, y) scale(zoom) */}
            <g transform={`translate(${x}, ${y}) scale(${zoom})`}>
                {clusterCenters.map((center, idx) => {
                    const meta = clusterMeta[idx];
                    if (!meta) return null;

                    const { cx, cy } = center;

                    // comment: find group meta to get icon and color
                    const groupId = meta.groupId;
                    const group =
                        groupId && gruposById
                            ? gruposById.get(String(groupId))
                            : null;
                    const icon =
                        group && group.icon
                            ? String(group.icon).trim() || "•"
                            : "•";
                    const bgColor =
                        group && group.color ? group.color : "#d32f2f";

                    return (
                        <g key={idx}>
                            {/* comment: background circle for the group icon */}
                            <circle
                                cx={cx}
                                cy={cy}
                                r={30}
                                fill={bgColor}
                                opacity={0.9}
                            />

                            {/* comment: group icon centered on cluster */}
                            <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={30}
                                fill="#ffffff"
                                fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                            >
                                {icon}
                            </text>

                            {/* comment: main ring */}
                            {meta.rMain > 0 && (
                                <circle
                                    cx={cx}
                                    cy={cy}
                                    r={meta.rMain}
                                    stroke="blue"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.35}
                                />
                            )}

                            {/* comment: extra ring */}
                            {meta.rExtra > 0 && (
                                <circle
                                    cx={cx}
                                    cy={cy}
                                    r={meta.rExtra}
                                    stroke="green"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.35}
                                />
                            )}
                        </g>
                    );
                })}
            </g>
        </svg>
    );
};

const CenterMarkerOverlay = () => {
    const { x, y, zoom } = useViewport();

    return (
        <svg
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 1,
            }}
        >
            <g transform={`translate(${x}, ${y}) scale(${zoom})`}>
                {/* comment: use canvasCenter as logical center */}
                <g transform={`translate(${canvasCenter}, ${canvasCenter})`}>
                    {/* comment: outer circle */}
                    <circle
                        cx={0}
                        cy={0}
                        r={1}
                        stroke="#000000FF"
                        strokeWidth={2}
                        // fill="none"
                        opacity={0.8}
                    />
                    {/* comment: crosshair lines */}
                    {/* <line
                        x1={-14}
                        y1={0}
                        x2={14}
                        y2={0}
                        stroke="#ff0000"
                        strokeWidth={1}
                    />
                    <line
                        x1={0}
                        y1={-14}
                        x2={0}
                        y2={14}
                        stroke="#ff0000"
                        strokeWidth={1}
                    /> */}
                </g>
            </g>
        </svg>
    );
};

const NodosBoard = ({
    personas = [],
    relaciones = [],
    grupos = [],
    gruposPersonas = [],
    selectedPersonaId = "",
    onSelectPersona,
    maxDistance = 420,
    spreadCap = 40,
    center = null,
    onConnectPair,
    onOpenPersona,
    locateTick = 0,
    layoutMode = "grid", // comment: "groups" | "grid"
}) => {
    const boardRef = useRef(null);
    const wrapperRef = useRef(null);
    const [rfInstance, setRfInstance] = useState(null);
    const [draggingNodeId, setDraggingNodeId] = useState(null);
    const [avatarCenters, setAvatarCenters] = useState(new Map());

    const handleAvatarCenterUpdate = useCallback((personaId, center) => {
        // comment: store center offset per persona
        setAvatarCenters((prev) => {
            const next = new Map(prev);
            next.set(String(personaId), center);
            return next;
        });
    }, []);

    const getAvatarCenterOffset = useCallback(
        (personaId) => avatarCenters.get(String(personaId)) || null,
        [avatarCenters]
    );

    // comment: compute the center based on the wrapper size if available
    const centerPoint = useMemo(() => {
        if (
            center &&
            typeof center.x === "number" &&
            typeof center.y === "number"
        ) {
            return center;
        }
        const el = wrapperRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            return {
                x: Math.floor(rect.width / 2),
                y: Math.floor(rect.height / 2),
            };
        }
        const w = typeof window !== "undefined" ? window.innerWidth : 1600;
        const h = typeof window !== "undefined" ? window.innerHeight : 900;
        return { x: Math.floor(w / 2), y: Math.floor(h / 2) };
    }, [center, wrapperRef.current]);

    const globalSeed = useMemo(() => {
        const key = (personas || [])
            .map((p) => String(p?.persona_id ?? ""))
            .sort()
            .join("|");
        return hashStringToInt(key);
    }, [personas]);

    const gruposById = useMemo(() => {
        const m = new Map();
        (grupos || []).forEach((g) => m.set(String(g.grupo_id), g));
        return m;
    }, [grupos]);
    const groupsByPersona = useMemo(() => {
        const m = new Map();
        (gruposPersonas || []).forEach((gp) => {
            const pid = String(gp.persona_id);
            const gid = String(gp.grupo_id);
            const g = gruposById.get(gid);
            if (!g) return;
            if (!m.has(pid)) m.set(pid, []);
            m.get(pid).push(g);
        });
        return m;
    }, [gruposPersonas, gruposById]);

    // NodosBoard.jsx
    const initialNodes = useMemo(() => {
        return personas.map((p) => {
            const id = String(p?.persona_id ?? "");
            return {
                id: String(p?.persona_id ?? ""),
                type: "nodoPersona",
                position: { x: canvasCenter, y: canvasCenter },
                // style: {
                //     transition: "transform 800ms ease-in-out",
                // },
                data: {
                    personaId: id,

                    displayName: p?.display_name || "",
                    photoUrl: p?.googlePicURL || "",
                    groups: (groupsByPersona.get(id) || []).map((g) => ({
                        grupo_id: g.grupo_id,
                        nombre: g.nombre,
                        color: g.color,
                        icon: g.icon,
                    })),
                    onAvatarCenterChange: handleAvatarCenterUpdate,
                },
            };
        });
    }, [personas, globalSeed, maxDistance, spreadCap, centerPoint]);

    const initialEdges = useMemo(() => {
        const pairCounts = new Map();
        return (relaciones || []).reduce((acc, r, i) => {
            const source = String(r.persona_a);
            const target = String(r.persona_b);
            if (source === target) return acc;
            const pairKey =
                source < target
                    ? `${source}--${target}`
                    : `${target}--${source}`;
            const idx = pairCounts.get(pairKey) || 0;
            pairCounts.set(pairKey, idx + 1);
            acc.push({
                id: `rel_${source}_${target}_${i}`,
                source,
                target,
                label: r.tipo || "",
                type: "floating",
                markerStart: r.reciproca
                    ? { type: MarkerType.ArrowClosed }
                    : undefined,
                markerEnd: { type: MarkerType.ArrowClosed },
                style: { opacity: 0.9 },
                data: { parallelIndex: idx, tipo: r.tipo || "" },
            });
            return acc;
        }, []);
    }, [relaciones]);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const [layoutTick, setLayoutTick] = useState(0);

    useEffect(() => setNodes(initialNodes), [initialNodes, setNodes]);
    useEffect(() => setEdges(initialEdges), [initialEdges, setEdges]);
    const [clusterMeta, setClusterMeta] = useState([]);
    const [clusterCenters, setClusterCenters] = useState([]);
    useEffect(() => {
        if (layoutMode === "groups") {
            const result = layoutPersonaNodes({
                personas,
                relaciones,
                gruposPersonas,
                grupos,
                setNodes,
                setLayoutTick,
                getAvatarCenterOffset,
            });

            if (result) {
                console.log({ result });
                setClusterMeta(result.clusterMeta);
                setClusterCenters(result.clusterCenters);
            }
        } else {
            setClusterMeta(null);
            setClusterCenters(null);
            layoutPersonaNodesGrid({
                setNodes,
                setLayoutTick,
                getAvatarCenterOffset,
            });
        }
    }, [personas, relaciones, gruposPersonas, grupos, setNodes, layoutMode]);

    const decoratedEdges = useMemo(() => {
        const sel = String(selectedPersonaId || "");
        const anySelected = !!sel;
        return edges.map((e) => {
            const connected =
                anySelected && (e.source === sel || e.target === sel);
            const baseLabel = e?.data?.tipo ?? e.label ?? "";
            const opacity = anySelected ? (connected ? 0.9 : 0.18) : 0.18;
            const label = anySelected ? (connected ? baseLabel : "") : "";
            return { ...e, style: { ...(e.style || {}), opacity }, label };
        });
    }, [edges, selectedPersonaId]);

    useEffect(() => {
        if (!rfInstance) return;
        if (nodes.length === 0) return;
        const id = requestAnimationFrame(() => {
            try {
                rfInstance.fitView({
                    padding: 0.2,
                    includeHiddenNodes: true,
                    duration: 800,
                });
            } catch (e) {
                console.warn("fitView failed:", e);
            }
        });
        return () => cancelAnimationFrame(id);
    }, [rfInstance, nodes.length, layoutTick]);

    // useEffect(() => {
    //     console.log(
    //         "RF nodes state",
    //         nodes.map((n) => ({
    //             id: n.id,
    //             x: n.position?.x,
    //             y: n.position?.y,
    //             type: n.type,
    //         }))
    //     );
    // }, [nodes]);

    // comment: center selected persona when it changes or when locateTick changes
    useEffect(() => {
        if (!rfInstance) return;
        if (!selectedPersonaId) return;

        const selId = String(selectedPersonaId);
        const node = nodes.find((n) => n.id === selId);
        if (!node) return;

        try {
            rfInstance.fitView({
                nodes: [node],
                padding: 0.6,
                includeHiddenNodes: true,
                duration: 800,
                minZoom: 0.4,
                maxZoom: 2,
            });
        } catch (e) {
            console.warn("fitView on node failed:", e);
        }
    }, [rfInstance, selectedPersonaId, nodes, locateTick]);

    return (
        <div ref={wrapperRef} style={{ width: "100%", height: "100vh" }}>
            <ReactFlow
                nodes={nodes.map((n) => {
                    const isSelected = n.id === String(selectedPersonaId);
                    const baseStyle = n.style || {};

                    // comment: animate positions only when not dragging
                    const shouldAnimate = !draggingNodeId;
                    const style = shouldAnimate
                        ? {
                              ...baseStyle,
                              transition: "transform 800ms ease-in-out",
                          }
                        : { ...baseStyle, transition: "none" };

                    return {
                        ...n,
                        selected: isSelected,
                        style,
                    };
                })}
                translateExtent={[
                    [-canvasSize, -canvasSize],
                    [canvasSize, canvasSize],
                ]}
                edges={decoratedEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionLineComponent={FloatingConnectionLine}
                nodesDraggable
                onInit={(instance) => {
                    setRfInstance(instance);
                    instance.setCenter(canvasCenter, canvasCenter, {
                        zoom: 1,
                        duration: 0,
                    });
                }}
                onNodeDragStart={(_, node) => {
                    // comment: disable transform transition while dragging
                    setDraggingNodeId(node.id);
                }}
                onNodeDragStop={() => {
                    // comment: re-enable transition after drag finishes
                    setDraggingNodeId(null);
                }}
                onSelectionChange={() => {}}
                onNodeClick={(_, node) =>
                    onSelectPersona && onSelectPersona(node.id)
                }
                onNodeDoubleClick={(_, node) => {
                    onSelectPersona && onSelectPersona(node.id);
                    onOpenPersona && onOpenPersona(node.id);
                }}
                onConnect={(c) => {
                    if (c?.source && c?.target && onConnectPair)
                        onConnectPair(c.source, c.target);
                }}
                onPaneClick={() => onSelectPersona && onSelectPersona("")}
                proOptions={{ hideAttribution: true }}
            >
                <MiniMap pannable zoomable />
                <Controls />
                <Background />
                <ViewportDebug />
                <ClusterRingsOverlay
                    clusterMeta={clusterMeta}
                    clusterCenters={clusterCenters}
                    gruposById={gruposById}
                />
                <CenterMarkerOverlay />
            </ReactFlow>
        </div>
    );
};

export default NodosBoard;
